import { FC, useContext, useRef, useState, useMemo, useCallback, useEffect } from "react";
import "./TaskPerson.css";
import Avatar, { AvatarSize } from "../../Shared/Avatar/Avatar";
import PersonLineDisplay from "../../Shared/PersonLineDisplay";
import { fullNameFormat, initials } from "../../../helpers/Inicials";
import UserPopupWrapper from "../../Shared/UserPopup/UserPopupWrapper";
import { TaskContext } from "../../../contexts/TaskContext";
import Button from "../../UiLib/Button/Button";
import { AppContext } from "../../../App";

import { ReactComponent as Cross } from "../../../assets/images/cross.svg";
import { ReactComponent as BluePen } from "../../../assets/editButtonGray.svg";
import PersonSelector from "../../Shared/PersonSelector/PersonSelector";
import WidgetContainer from "../../UiLib/WidgetContainer/WidgetContainer";
import useOutsideAlerter from "../../../hooks/useOutsideAlert";
import { IUserMinimal } from "../../../common/api_interfaces/user/IUserMinimal";
import { IPerson } from "../../../common/api_interfaces/task/IGetAllTaskPerson";

interface ITaskPerson {
    permission: boolean;
}

interface IPersonDisplayProps {
    user: {
        id: string;
        surname?: string;
        name?: string;
        middlename?: string;
    };
    showFullName?: boolean;
}

const PersonDisplay: FC<IPersonDisplayProps> = ({ user, showFullName = false }) => {
    const name = useMemo(
        () =>
            showFullName
                ? fullNameFormat(
                      {
                          surname: user.surname ?? "",
                          name: user.name ?? "",
                          middlename: user.middlename ?? "",
                      },
                      "s N M"
                  )
                : undefined,
        [user.surname, user.name, user.middlename, showFullName]
    );

    return (
        <UserPopupWrapper userId={Number(user.id)}>
            {showFullName ? (
                <PersonLineDisplay name={name ? name : "-"} userId={Number(user.id)} />
            ) : (
                <div className="task__person--block--img">
                    <Avatar userId={Number(user.id)} size={AvatarSize.s} />
                </div>
            )}
        </UserPopupWrapper>
    );
};

const TaskPerson: FC<ITaskPerson> = ({ permission }) => {
    const [showFullList, setShowFullList] = useState(false);
    const [openModalResponsible, setOpenModalResponsible] = useState(false);
    const [openModalGroup, setOpenModalGroup] = useState(false);

    const outsideAlerterResponsible = useOutsideAlerter<HTMLDivElement>(() => setOpenModalResponsible(false));
    const outsideAlerterGroup = useOutsideAlerter<HTMLDivElement>(() => setOpenModalGroup(false));

    const listRef = useRef<HTMLDivElement>(null);

    const { showToast } = useContext(AppContext);
    const taskContext = useContext(TaskContext);

    const handleMouseEnter = useCallback(() => setShowFullList(true), []);
    const handleMouseLeave = useCallback(() => setShowFullList(false), []);

    // Рендер выбора пользователей
    const renderPersonSelector = useCallback(
        (type: "group" | "responsible") => {
            const typeGroup = type === "group";

            console.log(taskContext.filteredChoiceGroup);

            const selectorProp = {
                updateFilters: typeGroup ? () => {} : () => {},
                loadPage: typeGroup ? () => {} : () => {},
                scrollEnded: typeGroup ? false : false,
                members: typeGroup ? taskContext.filteredChoiceGroup : taskContext.filteredChoiseExecutor,
                selectPerson: typeGroup ? taskContext.handleSelectGroup : taskContext.handleSelectExecutor,
                projectId: taskContext.taskInformation?.projectId ?? -1,
            };

            return <PersonSelector {...selectorProp} />;
        },
        [taskContext.taskInformation, taskContext.filteredChoiceGroup, taskContext.filteredChoiseExecutor]
    );

    const renderSubscriber = useCallback(
        (user: IUserMinimal) => {
            const userName = user.name ? fullNameFormat(user, "s N M") : initials((user as any).responsibleName);

            return (
                <div
                    className="task-person-group-current-user"
                    key={user.id}
                    title={"Удалить пользователя из подписчиков"}
                >
                    <PersonLineDisplay name={userName} userId={user.id} />
                    <Button
                        id={taskContext.editTask ? "button-cross" : "button-cross-gray"}
                        callBack={
                            taskContext.editTask
                                ? () => taskContext.handleRemoveGroup(user.id)
                                : () => showToast("У вас нет прав на редактирование задачи")
                        }
                        image={<Cross />}
                    />
                </div>
            );
        },
        [taskContext.editTask, taskContext.handleRemoveGroup, initials, fullNameFormat]
    );

    if (!taskContext.allPerson) return null;

    return (
        <div className="task-person-component">
            {/* Исполнитель */}
            <div className="task-person-block-component">
                <div className="task-person-block-head">
                    <h2>Исполнитель</h2>
                    <Button
                        id={taskContext.editTask && !taskContext.forceLinkExecutor ? "button-pen" : "button-pen-gray"}
                        callBack={
                            taskContext.editTask
                                ? !taskContext.forceLinkExecutor
                                    ? () => setOpenModalResponsible(true)
                                    : () => showToast("Исполнитель задачи синхронизирован с внешней системой")
                                : () => showToast("У вас нет прав на редактирование задачи")
                        }
                        image={<BluePen />}
                    />
                    {openModalResponsible && (
                        <div ref={outsideAlerterResponsible} className="task-person-block-responsible-block">
                            <WidgetContainer className="task-person-block">
                                <div className="task-person-block-responsible-head">
                                    <a>Исполнитель</a>
                                    <Button
                                        id="button-cross"
                                        callBack={() => setOpenModalResponsible(false)}
                                        image={<Cross />}
                                    />
                                </div>
                                {renderPersonSelector("responsible")}
                            </WidgetContainer>
                        </div>
                    )}
                </div>
                {taskContext.executor && (
                    <div className="task-person-block-body">
                        <PersonDisplay
                            user={{
                                id: String(taskContext.executor.id),
                                surname: taskContext.executor.surname,
                                name: taskContext.executor.name,
                                middlename: taskContext.executor.middlename,
                            }}
                            showFullName
                        />
                    </div>
                )}
            </div>

            {/* Подписчики */}
            <div className="task-person-block-component">
                <div className="task-person-block-head">
                    <h2>Подписчики - {taskContext.group?.length}</h2>
                    <Button
                        id={taskContext.editTask ? "button-pen" : "button-pen-gray"}
                        callBack={
                            taskContext.editTask
                                ? () => setOpenModalGroup(true)
                                : () => showToast("У вас нет прав на редактирование задачи")
                        }
                        image={<BluePen />}
                    />
                    {openModalGroup && (
                        <div ref={outsideAlerterGroup} className="task-person-block-group-component">
                            <WidgetContainer className="task-person-block">
                                <div className="task-person-block-head">
                                    <h2>Подписчики - {taskContext.group?.length}</h2>
                                    <Button
                                        id="button-cross"
                                        callBack={() => setOpenModalGroup(false)}
                                        image={<Cross />}
                                    />
                                </div>
                                <div className="task-person-group-block-minimal">
                                    {taskContext.group?.map((xx) => renderSubscriber(xx as IUserMinimal))}
                                </div>
                                <div className="task-person-block-head">
                                    <h2>Добавить</h2>
                                </div>
                                <div className="task-person-group-block">{renderPersonSelector("group")}</div>
                            </WidgetContainer>
                        </div>
                    )}
                </div>
                {permission && taskContext.group && taskContext.group.length > 0 && (
                    <div
                        className="task-person-block-line-group"
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        ref={listRef}
                    >
                        {taskContext.visibleGroup?.map((item) => (
                            <div key={`person-${item.id}`} className="task-person-block-name-person-group">
                                <PersonDisplay
                                    user={{
                                        id: String(item.id),
                                        surname: item.surname,
                                        name: item.name,
                                        middlename: item.middlename,
                                    }}
                                />
                            </div>
                        ))}

                        {showFullList &&
                            taskContext.hiddenGroup?.map((item) => (
                                <div key={`person-${item.id}`} className="task-person-block-name-person-group">
                                    <PersonDisplay
                                        user={{
                                            id: String(item.id),
                                            surname: item.surname,
                                            name: item.name,
                                            middlename: item.middlename,
                                        }}
                                    />
                                </div>
                            ))}

                        {taskContext.hasMore && !showFullList && (
                            <div className="task-person-block-more">+{taskContext.hiddenGroup?.length}</div>
                        )}
                    </div>
                )}
            </div>

            {/* Автор */}
            <div className="task-person-block-component">
                <div className="task-person-block-head">
                    <h2>Автор</h2>
                </div>
                {taskContext.allPerson?.author && (
                    <div className="task-person-block-body">
                        <PersonDisplay
                            user={{
                                id: String(taskContext.allPerson?.author.id),
                                surname: taskContext.allPerson?.author.surname,
                                name: taskContext.allPerson?.author.name,
                                middlename: taskContext.allPerson?.author.middlename,
                            }}
                            showFullName
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaskPerson;
